<template>
  <div
    class="steps-bar"
    :class="`steps-bar--align-${step_alignment}`"
    :style="steps_style"
  >
    <div
      v-for="(step, index) in steps"
      :key="step.id"
      class="steps-bar__item"
      :class="{
        'steps-bar__item--active': step.id === active_id,
        'steps-bar__item--completed': is_completed(step.id),
        'steps-bar__item--disabled': !is_enabled(step.id),
        'steps-bar__item--clickable': allow_navigation && is_enabled(step.id),
      }"
    >
      <button
        type="button"
        class="steps-bar__group"
        :disabled="!allow_navigation || !is_enabled(step.id)"
        :aria-current="step.id === active_id ? 'step' : null"
        @click="handle_click(step)"
      >
        <vb-icon
          v-if="is_completed(step.id)"
          class="steps-bar__symbol"
          :name="'check'"
          :color="'ffffff'"
          :size="16"
        />
        <span v-else class="steps-bar__symbol">{{ index + 1 }}</span>
        <span class="steps-bar__label">{{ step.name }}</span>
      </button>
      <span
        v-if="index !== steps.length - 1"
        :class="{
          'steps-bar__line': true,
          'steps-bar__line--completed': is_completed(step.id),
        }"
      ></span>
    </div>
  </div>
</template>

<script>
  export default {
    name: 'vb-steps-bar',
    props: {
      steps: {
        type: Array,
        required: true,
      },
      active_id: {
        type: String,
        default: '',
      },
      completed_ids: {
        type: Array,
        default: () => [],
      },
      enabled_ids: {
        type: Array,
        default: null,
      },
      allow_navigation: {
        type: Boolean,
        default: true,
      },
      customizations: {
        type: Object,
        default: () => ({}),
      },
    },
    computed: {
      step_color() {
        const { steps_color = 'var(--primary)' } = this.customizations;
        return steps_color;
      },
      step_alignment() {
        const { steps_alignment = 'row' } = this.customizations;
        return steps_alignment;
      },
      symbol_size() {
        const { symbol_size = '25px' } = this.customizations;
        return symbol_size;
      },
      line_thickness() {
        const { line_thickness = '2.5px' } = this.customizations;
        return line_thickness;
      },
      steps_style() {
        return {
          '--step-color': this.step_color,
          '--step-alignment': this.step_alignment,
          '--symbol-size': this.symbol_size,
          '--line-thickness': this.line_thickness,
        };
      },
    },
    methods: {
      is_completed(id) {
        return this.completed_ids.indexOf(id) >= 0;
      },
      is_enabled(id) {
        if (!this.enabled_ids) return true;
        if (id === this.active_id) return true;
        return this.enabled_ids.indexOf(id) >= 0;
      },
      handle_click(step) {
        if (!this.allow_navigation) return;
        if (step.id === this.active_id) return;
        if (!this.is_enabled(step.id)) return;
        this.$emit('select', step.id);
      },
    },
  };
</script>

<style lang="scss" scoped>
  .steps-bar {
    background-color: $white;
    border-radius: 10px;
    padding: 15px 20px;
    display: flex;
    align-items: center;
    position: relative;
    margin-bottom: $grid-gutter-width/2;

    * {
      transition: 250ms;
    }

    &__group {
      // reset native <button> chrome — the group is the focusable/clickable
      // element, but must lay out as the symbol + label flex row
      appearance: none;
      background: none;
      border: 0;
      padding: 0;
      margin: 0;
      color: inherit;
      font: inherit;
      display: flex;
      align-items: center;
      flex-direction: var(--step-alignment, row);
      gap: 10px;
    }

    &__item {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: flex-start;

      &:last-child {
        width: min-content;
        justify-content: center;
        margin-right: $grid-gutter-width * 2;
      }

      &--clickable {
        .steps-bar__group {
          cursor: pointer;
        }

        .steps-bar__group:hover {
          .steps-bar__symbol {
            box-shadow: 0px 0px 7.5px var(--step-color);
          }
          .steps-bar__label {
            text-decoration: underline;
          }
        }
      }

      &--disabled {
        opacity: 0.45;
        .steps-bar__group {
          cursor: not-allowed;
        }
      }

      &--active {
        .steps-bar__label {
          text-decoration: underline;
        }
        .steps-bar__symbol {
          box-shadow: 0px 0px 7.5px var(--step-color);
        }
      }
    }

    &__symbol {
      background-color: var(--step-color);
      border-radius: 50%;
      min-width: var(--symbol-size, 25px);
      min-height: var(--symbol-size, 25px);
      max-width: var(--symbol-size, 25px);
      max-height: var(--symbol-size, 25px);
      color: $white;
      font-size: $font-size-md;
      font-weight: $vb-font-weight-semibold;
      display: flex;
      align-items: center;
      justify-content: center;

      .steps-bar__item--completed & {
        background-color: var(--step-color);
      }
    }

    &__label {
      font-weight: $vb-font-weight-semibold;
    }

    &__line {
      margin-right: $grid-gutter-width/3;
      margin-left: $grid-gutter-width/3;
      min-height: var(--line-thickness, 2.5px);
      flex-grow: 1;
      background-color: $gray-300;
      &--completed {
        background-color: var(--step-color);
      }
    }

    // when the label stacks above/below the symbol, keep the connector line
    // level with the symbol instead of centered on the whole group
    &--align-column {
      .steps-bar__item {
        align-items: flex-start;
      }
      .steps-bar__line {
        margin-top: calc(
          (var(--symbol-size, 25px) - var(--line-thickness, 2.5px)) / 2
        );
      }
    }

    &--align-column-reverse {
      .steps-bar__item {
        align-items: flex-end;
      }
      .steps-bar__line {
        margin-bottom: calc(
          (var(--symbol-size, 25px) - var(--line-thickness, 2.5px)) / 2
        );
      }
    }
  }
</style>
